import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { getPurchaseOrdersManagementRequest } from "../../slices/managementSlices/purchaseOrdersSlice";
import PODetailsSidebar from "./PODetailsSidebarNew";
import { Search, Edit, Trash2, Plus } from "lucide-react";
import { PlusIcon } from "@heroicons/react/24/outline";
import PurchaseOrderSidebar from "../../components/PurchaseOrderSidebar";
import StatusFilter from "../../components/StatusFilter";
import { SelectDropdown } from "../../components/SelectDropdown";
import CSVExportButton from "../../components/reports/CSVExportButton";
import { statuses } from "../../consts/status";
import PurchaseOrderStatusChip from "./PurchaseOrderStatusChip";
import { SORT_DIRECTION } from "../../consts/general";
import { sortablePurchaseOrdersTableColumns } from "../../consts/table";
import { deletePurchaseOrder, PurchaseOrdersManagementLoadingKeys } from "../../slices/managementSlices/purchaseOrdersSlice";
import { ConfrimModal } from "../../components/ConfrimModal";
import { LoaderSpinner } from "../../components/LoaderSpinner";
import { formatCurrencyUsdFixedTwo } from "../../helpers/common";
import { useDebounce } from "../../helpers/useDebounce";
import { toast } from "react-toastify";
import { ROUTES } from "../../consts/general";

const getPurchaseOrderRef = (poNumber, id) => {
  const match = String(poNumber || "").trim().match(/^PO\s+(\d+)$/i);
  return match ? match[1] : String(id || "").trim();
};

const formatPurchaseOrderDate = (value) => {
  if (!value) return value || "";

  const rawValue = String(value);
  const date = /^\d{4}-\d{2}-\d{2}$/.test(rawValue)
    ? new Date(`${rawValue}T00:00:00`)
    : new Date(rawValue);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

export default function PurchaseOrders({
  access
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { poRef } = useParams();
  const { purchaseOrdersManagement, loadingKeys, loading, error, hasLoaded } = useSelector(
    (state) => state.purchaseOrdersManagement,
  );

  const [selectedPOId, setSelectedPOId] = useState(null);
  const [sidebarMode, setSidebarMode] = useState('view'); // 'view' | 'edit'
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorsOptions, setVendorsOptions] = useState([]);
  const [sortBy, setSortBy] = useState('po_date');
  const [sortDirection, setSortDirection] = useState('DESC');
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [poIdToDelete, setPoIdToDelete] = useState(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 350);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(prev =>
        prev === SORT_DIRECTION.ASCENDING ? SORT_DIRECTION.DESCENDING : SORT_DIRECTION.ASCENDING,
      );
    } else {
      setSortBy(column);
      setSortDirection(SORT_DIRECTION.ASCENDING);
    }
  };

  const getSortClass = (column) => {
    if (sortBy !== column) return '';
    return sortDirection === SORT_DIRECTION.ASCENDING ? 'triangle-up' : 'triangle-down';
  };

  const fetchPurchaseOrders = () => {
    dispatch(
      getPurchaseOrdersManagementRequest({
        search: debouncedSearchTerm,
        page: 1,
        itemsPerPage: 100,
        sortBy,
        sortDirection,
        filters: {
          eden_status: "ACTIVE",
          ...(selectedVendor ? { vendor_id: selectedVendor } : {}),
          ...(selectedStatuses.length ? { status: selectedStatuses } : {}),
        },
      }),
    );
  };

  const isDeletePOLoading = () => loadingKeys.includes(PurchaseOrdersManagementLoadingKeys.deletePurchaseOrder);

  useEffect(() => {
    axios
      .get('/api/v1/management/vendors-options')
      .then(({ data }) => setVendorsOptions(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchPurchaseOrders();
  }, [debouncedSearchTerm, selectedVendor, selectedStatuses, sortBy, sortDirection]);

  useEffect(() => {
    let cancelled = false;
    if (!poRef) {
      setSelectedPOId(null);
      return undefined;
    }
    axios.get(`/api/v1/management/purchase-order-ref/${encodeURIComponent(poRef)}`)
      .then(({ data }) => {
        if (cancelled) return;
        setSelectedPOId(data.id);
        const canonicalRef = getPurchaseOrderRef(data.po_number, data.id);
        if (canonicalRef && canonicalRef !== poRef) {
          navigate(`${ROUTES.PURCHASE_ORDERS}/${encodeURIComponent(canonicalRef)}`, { replace: true });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setSelectedPOId(null);
        toast.error("Purchase order not found.", { position: "bottom-right" });
        navigate(ROUTES.PURCHASE_ORDERS, { replace: true });
      })
    return () => { cancelled = true; };
  }, [poRef, navigate]);

  const openPurchaseOrder = (po) => {
    setSidebarMode('view');
    navigate(`${ROUTES.PURCHASE_ORDERS}/${encodeURIComponent(getPurchaseOrderRef(po.poNumber, po.id))}`);
  };

  const filteredPOs = purchaseOrdersManagement || [];

  return (
    <div className="flex h-dvh overflow-hidden bg-background">
      <div
        className={`flex-1 min-w-0 overflow-y-auto transition-all duration-300 ${selectedPOId ? "mr-[880px] max-[639px]:mr-0" : ""}`}
      >
        <div className="p-6 max-[639px]:p-3">
          <div className="mb-6 flex min-h-14 max-w-full flex-wrap items-center gap-x-4 gap-y-2 max-[639px]:min-h-12">
            <div className="max-w-full flex-none">
              <div className="flex max-w-full flex-wrap items-baseline gap-x-2 gap-y-1">
                <h1 className="min-w-0 text-xl font-bold text-gray-900">Purchase Orders</h1>
                <span className="shrink-0 text-sm text-gray-500">Management</span>
              </div>
            </div>
            {access === "edit" && (
              <button
                className="ml-auto flex h-8 shrink-0 min-w-max flex-row items-center gap-1 whitespace-nowrap rounded-md bg-gray-900 px-2.5 text-sm font-semibold text-white transition-colors hover:bg-gray-800 max-[639px]:hidden"
                onClick={() => setIsCreateOpen(true)}
              >
                <PlusIcon className="h-3.5 w-3.5 shrink-0 text-white" />
                <span className="text-sm font-semibold text-white">Create Purchase order</span>
              </button>
            )}
          </div>

          <div className="mb-6 flex w-full flex-wrap items-center gap-4 max-[639px]:block">
            <div className="relative w-72 max-w-full min-w-0 flex-none max-[639px]:mb-3 max-[639px]:w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                placeholder="Search purchase orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-8 pl-10 border rounded-md w-full text-sm"
              />
            </div>
            <div className="flex min-w-[220px] flex-1 flex-wrap items-center gap-4 max-[639px]:w-full max-[639px]:min-w-0 max-[639px]:flex-nowrap max-[639px]:overflow-x-auto max-[639px]:[scrollbar-width:none]">
              <div className="flex shrink-0 items-center gap-2">
                <StatusFilter
                  options={statuses}
                    selectedOptions={selectedStatuses}
                    onChange={setSelectedStatuses}
                  placeholder="Status"
                  portal
                  purchaseOrderPalette
                  triggerClassName="!h-8 !px-3 !py-0 !text-sm"
                />
                  <div className="inline-block max-w-[220px] min-w-0 shrink-0">
                    <SelectDropdown
                      options={vendorsOptions}
                      value={selectedVendor}
                      onChange={setSelectedVendor}
                      placeholder="Supplier"
                      portal
                      expandMenuWidth
                      mainWrapperClassname="!w-auto max-w-[220px]"
                      selectClassname="!h-8 !w-auto !min-w-[100px] !max-w-[220px] !rounded-md !px-3 !py-0 !text-sm"
                      selectedValueClassname="!max-w-[150px] !truncate"
                    />
                  </div>
                </div>
                <div className="ml-auto flex shrink-0 items-center gap-4">
                  <span className="text-sm text-gray-500">{filteredPOs.length} orders</span>
                  <CSVExportButton
                    data={filteredPOs}
                    columns={[
                      { key: 'poNumber', label: 'PO Number' },
                      { key: 'vendor', label: 'Supplier' },
                      { key: 'status', label: 'Status' },
                      { key: 'totalValue', label: 'Total' },
                      { key: 'poDate', label: 'Date Created' },
                    ]}
                    filename="purchase_orders"
                    variant="outline"
                    className="!h-8 !px-3 !py-0 !text-sm"
                    iconOnlyOnMobile
                  />
                </div>
            </div>
          </div>

          {error && (
            <div
              className="mb-6 flex items-center justify-between gap-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              role="alert"
            >
              <span>{typeof error === 'string' ? error : 'Unable to load purchase orders.'}</span>
              <button
                type="button"
                className="shrink-0 font-medium underline hover:no-underline"
                onClick={fetchPurchaseOrders}
              >
                Retry
              </button>
            </div>
          )}

          {!hasLoaded && loading && (
            <div className="mb-6 flex justify-center">
              <LoaderSpinner width="32" height="32" border="3" colorSpinner="black" />
            </div>
          )}

          <div className="hidden min-[640px]:block border rounded-lg overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200">
                  <th
                    className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer"
                    onClick={() => handleSort(sortablePurchaseOrdersTableColumns.poNumber)}
                  >
                    <div className="flex items-center">
                      Order <span className={getSortClass(sortablePurchaseOrdersTableColumns.poNumber)}></span>
                    </div>
                  </th>
                  <th
                    className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer"
                    onClick={() => handleSort(sortablePurchaseOrdersTableColumns.vendorName)}
                  >
                    <div className="flex items-center">
                      Supplier <span className={getSortClass(sortablePurchaseOrdersTableColumns.vendorName)}></span>
                    </div>
                  </th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Total</th>
                  <th
                    className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer"
                    onClick={() => handleSort(sortablePurchaseOrdersTableColumns.poDate)}
                  >
                    <div className="flex items-center">
                      Date Created <span className={getSortClass(sortablePurchaseOrdersTableColumns.poDate)}></span>
                    </div>
                  </th>
                  {access === "edit" && (
                    <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredPOs.map((po) => (
                  <tr
                    key={po.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => {
                      openPurchaseOrder(po);
                    }}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-base font-bold text-gray-900">{po.poNumber}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{po.vendor}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <PurchaseOrderStatusChip status={po.status} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium text-gray-900 tabular-nums">{formatCurrencyUsdFixedTwo(po.totalValue)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatPurchaseOrderDate(po.poDate)}</td>
                    {access === "edit" && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                            <button
                              className="h-7 w-7 flex items-center justify-center hover:bg-gray-100 rounded"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSidebarMode('edit');
                                navigate(`${ROUTES.PURCHASE_ORDERS}/${encodeURIComponent(getPurchaseOrderRef(po.poNumber, po.id))}`);
                              }}
                            >
                              <Edit className="h-3.5 w-3.5 text-gray-900" />
                            </button>
                            <button
                              className="h-7 w-7 flex items-center justify-center hover:bg-red-50 rounded"
                              onClick={(e) => {
                                e.stopPropagation();
                                setPoIdToDelete(po.id);
                                setDeleteModalOpened(true);
                              }}
                            >
                              {isDeletePOLoading() && poIdToDelete === po.id ? (
                                <LoaderSpinner width='15' height='15' border='2' colorSpinner='red' />
                              ) : (
                                <Trash2 className="h-3.5 w-3.5 text-red-600" />
                                )}
                              </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="divide-y divide-gray-200 min-[640px]:hidden">
            {filteredPOs.map((po) => (
              <div key={po.id} className="bg-white px-1 py-4 text-base" onClick={() => openPurchaseOrder(po)}>
                <div className="flex justify-between gap-3"><span className="font-semibold">{po.poNumber}</span><span className="font-semibold">{formatCurrencyUsdFixedTwo(po.totalValue)}</span></div>
                <div className="mt-2 flex items-center justify-between text-sm text-gray-600">
                  <PurchaseOrderStatusChip status={po.status} /><span className="text-gray-500">{formatPurchaseOrderDate(po.poDate)}</span>
                </div>
                <div className="mt-2 truncate text-sm text-gray-500">{po.vendor}</div>
                {access === "edit" && <div className="mt-3 flex justify-end gap-2">
                  <button className="h-7 w-7 hover:bg-gray-100 rounded" onClick={(e) => { e.stopPropagation(); setSidebarMode('edit'); navigate(`${ROUTES.PURCHASE_ORDERS}/${encodeURIComponent(getPurchaseOrderRef(po.poNumber, po.id))}`); }}><Edit className="h-3.5 w-3.5 text-gray-900" /></button>
                  <button className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setPoIdToDelete(po.id); setDeleteModalOpened(true); }}><Trash2 className="h-3.5 w-3.5 text-red-600" /></button>
                </div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {selectedPOId && (
        <div className="fixed right-0 top-0 h-full w-[880px] max-w-full max-[639px]:top-14 max-[639px]:h-[calc(100dvh-3.5rem)] max-[639px]:w-full bg-white border-l shadow-lg z-50">
          <PODetailsSidebar
            purchaseOrderId={selectedPOId}
            initialMode={sidebarMode}
            access={access}
            onClose={() => navigate(ROUTES.PURCHASE_ORDERS)}
            onModeChange={setSidebarMode}
            onUpdate={fetchPurchaseOrders}
            onCanonicalIdChange={(id, poNumber, url) => {
              setSelectedPOId(id);
              const ref = getPurchaseOrderRef(poNumber, id);
              if (url) {
                const path = new URL(url).pathname;
                navigate(path, { replace: true });
              } else if (ref) {
                navigate(`${ROUTES.PURCHASE_ORDERS}/${encodeURIComponent(ref)}`, { replace: true });
              }
            }}
          />
        </div>
      )}
      {access === "edit" && (
        <button aria-label="Create purchase order" className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-gray-900 text-white shadow-lg min-[640px]:hidden" onClick={() => setIsCreateOpen(true)}>
          <Plus className="h-6 w-6" />
        </button>
      )}

      {isCreateOpen && (
        <PurchaseOrderSidebar
          variant="create"
          isOpen={access === "edit" && isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          onSuccessSubmit={() => {
            setIsCreateOpen(false);
            fetchPurchaseOrders();
          }}
        />
      )}

      <ConfrimModal
        isOpen={access === "edit" && deleteModalOpened}
        title={"Are you sure you want to delete this purchase order?"}
        confirmButtonTitle={"Delete"}
        isLoading={isDeletePOLoading()}
        submitClick={async () => {
          setDeleteModalOpened(false);
          await dispatch(deletePurchaseOrder({
            id: poIdToDelete,
            search: searchTerm,
            page: 1,
            itemsPerPage: 100,
            sortBy,
            sortDirection,
            filters: {
              eden_status: "ACTIVE",
              ...(selectedVendor ? { vendor_id: selectedVendor } : {}),
              ...(selectedStatuses.length ? { status: selectedStatuses } : {}),
            },
          }));
          setPoIdToDelete(null);
        }}
        cancelClick={() => {
          setDeleteModalOpened(false);
          setPoIdToDelete(null);
        }}
      />

    </div>
  );
}
